import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, takeUntil } from 'rxjs';

import { PCXConstants } from '../../sc-config/constant/PCXConstants';
import { BusinessService } from './business.service';
import { SeedMetaData } from '../models/domain/SeedMetaData';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from './common.service';
import { TranslateSeedDataPipe } from '../pipes/translate-seed-data.pipe';
import { ServiceItemTotalPriceMismatchReason } from '../../app-create-case/models/insurance-case.model';
import { CaseCreateService } from '../../app-create-case/services/case-create.service';
import { MailAuthStatusService } from './mail-auth-status.service';

@Injectable({
    providedIn: 'root'
})
export class DataStoreService {
    readonly isSeedDataLoaded = signal(false);

    testingCategories = new BehaviorSubject<SeedMetaData[]>(null);
    liabilityAssessmentTypes = new BehaviorSubject<SeedMetaData[]>(null);
    caseTrades = new BehaviorSubject<SeedMetaData[]>(null);
    caseTypes = new BehaviorSubject<SeedMetaData[]>(null);
    // Add new BehaviorSubject for total price mismatch reasons
    serviceItemTotalPriceMismatchReasons = new BehaviorSubject<ServiceItemTotalPriceMismatchReason[]>(null);
    inspectorEmailAuthStatus = new BehaviorSubject<any>(null);
    // inspector whose status fetch is still running; null when nothing is pending
    private pendingInspectorUserId: string = null;
    constructor(
        private readonly businessService: BusinessService,
        private readonly translateService: TranslateService,
        private readonly caseCreateService: CaseCreateService,
        private readonly mailAuthStatusService: MailAuthStatusService
    ) {
        this.translateService.onLangChange.subscribe(lanChangeEvent => {
            const languageKey = CommonService.getLanguageKeyFromLang(this.translateService.currentLang);
            const testingCategories = this.testingCategories.getValue();
            this.testingCategories.next(
                testingCategories.sort((a, b) => {
                    return SeedMetaData.Compare(a, b, languageKey);
                })
            );

            const liabilityAssessmentTypes = this.liabilityAssessmentTypes.getValue();
            liabilityAssessmentTypes.map(liabilityAssessmentType => {
                const TranslatedliabilityAssessmentType = new TranslateSeedDataPipe().transform(
                    liabilityAssessmentType.Key,
                    lanChangeEvent?.lang,
                    liabilityAssessmentTypes,
                    true
                );
                liabilityAssessmentType.TranslatedValue = TranslatedliabilityAssessmentType;
                return liabilityAssessmentType;
            });
            this.liabilityAssessmentTypes.next(
                liabilityAssessmentTypes.sort((a, b) => {
                    return SeedMetaData.Compare(a, b, languageKey);
                })
            );

            const caseTrades = this.caseTrades.getValue();
            this.caseTrades.next(
                caseTrades.sort((a, b) => {
                    return SeedMetaData.Compare(a, b, languageKey);
                })
            );

            const caseTypes = this.caseTypes.getValue();
            caseTypes.map(caseType => {
                const translatedCaseType = new TranslateSeedDataPipe().transform(
                    caseType.Key,
                    lanChangeEvent?.lang,
                    caseTypes,
                    true
                );
                caseType.TranslatedValue = translatedCaseType;
                return caseType;
            });
            this.caseTypes.next(
                caseTypes.sort((a, b) => {
                    return SeedMetaData.Compare(a, b, languageKey);
                })
            );
        });

        this.mailAuthStatusService.mailAuthorized$.subscribe(userId => this.onMailAuthorized(userId));
    }

    storeSeedDatas(): void {
        void this.storeSeedDatasAsync();
    }

    storeSeedDatasAsync(): Promise<void> {
        if (this.isSeedDataLoaded()) {
            return Promise.resolve();
        }

        return firstValueFrom(
            this.businessService.getGenericEntityData('SeedMetaDatas', `{ IsMarkedToDelete: { $exists: true } }`)
        )
            .then(res => {
                const isoRegex = /ISODate\((".+?")\)/g;
                let responseData = res.Data;
                responseData = responseData.replace(isoRegex, function (match, parenGroup) {
                    return parenGroup;
                });
                responseData = responseData.replaceAll('"_id"', '"ItemId"');
                const response = JSON.parse(responseData) as SeedMetaData[];
                const languageKey = CommonService.getLanguageKeyFromLang(this.translateService.currentLang);
                this.testingCategories.next(
                    response
                        .filter(item => item.EntityName === PCXConstants.SeedEntityName.TestingCategorySeed)
                        .sort((a, b) => {
                            return SeedMetaData.Compare(a, b, languageKey);
                        })
                );

                const liabilityAssessmentTypes = response
                    .filter(item => item.EntityName === PCXConstants.SeedEntityName.LiabilityAssesmentSeed)
                    .sort((a, b) => {
                        return SeedMetaData.Compare(a, b, languageKey);
                    });

                liabilityAssessmentTypes.map(liabilityAssessmentType => {
                    const TranslatedLiabilityAssessmentType = new TranslateSeedDataPipe().transform(
                        liabilityAssessmentType.Key,
                        this.translateService.currentLang,
                        liabilityAssessmentTypes,
                        true
                    );
                    liabilityAssessmentType.TranslatedValue = TranslatedLiabilityAssessmentType;
                    return liabilityAssessmentType;
                });
                this.liabilityAssessmentTypes.next(liabilityAssessmentTypes);

                this.caseTrades.next(
                    response
                        .filter(item => item.EntityName === PCXConstants.SeedEntityName.TradeSeed)
                        .sort((a, b) => {
                            return SeedMetaData.Compare(a, b, languageKey);
                        })
                );
                const caseTypes = response
                    .filter(item => item.EntityName === PCXConstants.SeedEntityName.CaseType)
                    .sort((a, b) => {
                        return SeedMetaData.Compare(a, b, languageKey);
                    });
                caseTypes.map(caseType => {
                    const translatedCaseType = new TranslateSeedDataPipe().transform(
                        caseType.Key,
                        this.translateService.currentLang,
                        caseTypes,
                        true
                    );
                    caseType.TranslatedValue = translatedCaseType;
                    return caseType;
                });
                this.caseTypes.next(caseTypes);
                this.isSeedDataLoaded.set(true);
            })
            .catch(error => {
                console.error('Error when loading seed meta data', error);
            });
    }

    public getServiceSubItemTotalPriceMismatchReason(): Observable<ServiceItemTotalPriceMismatchReason[]> {
        const cachedData = this.serviceItemTotalPriceMismatchReasons.getValue();
        if (cachedData) {
            return this.serviceItemTotalPriceMismatchReasons.asObservable();
        }

        this.caseCreateService.getServiceSubItemTotalPriceMismatchReason().subscribe(response => {
            this.serviceItemTotalPriceMismatchReasons.next(response.Results);
        });

        return this.serviceItemTotalPriceMismatchReasons.asObservable();
    }

    public clearServiceItemTotalPriceMismatchReason(): void {
        this.serviceItemTotalPriceMismatchReasons.next(null);
    }

    public getInspectorEmailAuthStatus(inspectorUserId: string): Observable<any> {
        if (!inspectorUserId) {
            this.pendingInspectorUserId = null;
            this.inspectorEmailAuthStatus.next({
                InspectorUserId: inspectorUserId,
                AuthorizationStatus: false
            });

            return this.inspectorEmailAuthStatus.asObservable();
        }

        const cachedData = this.inspectorEmailAuthStatus.getValue();
        if (cachedData?.InspectorUserId === inspectorUserId) {
            // the cached inspector is wanted again, so a fetch still running for another one is not
            this.pendingInspectorUserId = null;
            return this.inspectorEmailAuthStatus.asObservable();
        }

        this.pendingInspectorUserId = inspectorUserId;
        this.mailAuthStatusService.getEmailAuthStatus(inspectorUserId).subscribe(response => {
            // Skip the response if it is no longer wanted: either another inspector was requested
            // meanwhile, or the authorization event already set this inspector's status.
            if (this.pendingInspectorUserId !== inspectorUserId) {
                return;
            }
            this.pendingInspectorUserId = null;
            const responseData = {
                AuthorizationStatus: response.Results['AuthorizationStatus'],
                InspectorUserId: inspectorUserId
            };
            this.inspectorEmailAuthStatus.next(responseData);
        });

        return this.inspectorEmailAuthStatus.asObservable();
    }

    private onMailAuthorized(userId: string): void {
        // A fetch for another inspector is running, so the form has moved on from this one.
        // That fetch's response will replace the cache anyway.
        if (!userId || (this.pendingInspectorUserId && this.pendingInspectorUserId !== userId)) {
            return;
        }

        const cachedData = this.inspectorEmailAuthStatus.getValue();
        const isFetchPending = this.pendingInspectorUserId === userId;
        const isCachedAsNotAuthorized = cachedData?.InspectorUserId === userId && !cachedData.AuthorizationStatus;

        if (!isFetchPending && !isCachedAsNotAuthorized) {
            return;
        }

        this.pendingInspectorUserId = null;
        this.inspectorEmailAuthStatus.next({
            AuthorizationStatus: true,
            InspectorUserId: userId
        });
    }
}
